import Script from "next/script";
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import ReactDOM from "react-dom";

import type { YMapsContextValue } from "../types";

const YMapsContext = createContext<YMapsContextValue>({
  isLoaded: false,
  error: null,
  YMap: null,
  YMapDefaultSchemeLayer: null,
  YMapDefaultFeaturesLayer: null,
  YMapMarker: null,
  YMapListener: null,
  YMapControls: null,
  ZoomControl: null,
  GeolocationControl: null,
  reactify: null,
  setCenter: () => {},
  location: null,
});

interface YMapsProviderProps {
  children: ReactNode;
  initialCenter?: [number, number];
  initialZoom?: number;
  initialBounds?: [[number, number], [number, number]]; // [[minLng, minLat], [maxLng, maxLat]]
}

export const useYMaps = () => useContext(YMapsContext);

export const YMapsProvider: React.FC<YMapsProviderProps> = ({
  children,
  initialCenter = [43.839767, 42.662089],
  initialZoom = 18,
  initialBounds,
}) => {
  const [contextValue, setContextValue] = useState<YMapsContextValue>({
    isLoaded: false,
    error: null,
    YMap: null,
    YMapDefaultSchemeLayer: null,
    YMapDefaultFeaturesLayer: null,
    YMapMarker: null,
    YMapListener: null,
    YMapControls: null,
    ZoomControl: null,
    GeolocationControl: null,
    reactify: null,
    setCenter: () => {},
    location: null,
  });

  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Определяем начальное состояние location
  const getInitialLocation = () => {
    if (initialBounds) {
      // Если переданы bounds, используем их - zoom НЕ НУЖЕН!
      return {
        bounds: initialBounds,
        // zoom: initialZoom, // ← НЕ ПЕРЕДАЕМ zoom с bounds!
      };
    } else {
      // Иначе используем center и zoom
      return {
        center: initialCenter,
        zoom: initialZoom,
      };
    }
  };

  const [location, setLocationState] = useState(getInitialLocation());

  useEffect(() => {
    // Keep internal state in sync with initial* props ONLY when switching
    // between bounds-driven mode and center/zoom-driven mode.
    //
    // Syncing for every bounds change can cause jerky animations because
    // LocationMap already controls the map via `setCenter({ bounds })`.
    setLocationState((prev: any) => {
      const next = getInitialLocation();
      if (!prev || !next) return next;

      const prevIsBounds = "bounds" in prev;
      const nextIsBounds = "bounds" in next;
      if (prevIsBounds === nextIsBounds) {
        return prev;
      }

      if ("bounds" in prev && "bounds" in next) {
        const prevB = prev.bounds;
        const nextB = next.bounds;
        const sameBounds =
          Array.isArray(prevB) &&
          Array.isArray(nextB) &&
          prevB.length === 2 &&
          nextB.length === 2 &&
          prevB[0]?.[0] === nextB[0]?.[0] &&
          prevB[0]?.[1] === nextB[0]?.[1] &&
          prevB[1]?.[0] === nextB[1]?.[0] &&
          prevB[1]?.[1] === nextB[1]?.[1];

        return sameBounds ? prev : next;
      }

      if ("center" in prev && "center" in next) {
        const prevC = prev.center;
        const nextC = next.center;
        const sameCenter =
          Array.isArray(prevC) &&
          Array.isArray(nextC) &&
          prevC.length === 2 &&
          nextC.length === 2 &&
          prevC[0] === nextC[0] &&
          prevC[1] === nextC[1];

        const prevZ = prev.zoom;
        const nextZ = next.zoom;
        const sameZoom =
          typeof prevZ === "number" &&
          typeof nextZ === "number" &&
          prevZ === nextZ;

        return sameCenter && sameZoom ? prev : next;
      }

      // switching between bounds and center/zoom
      return next;
    });
  }, [initialBounds, initialCenter, initialZoom]);

  const setCenter = useCallback((next: any) => {
    setLocationState((prev: any) => {
      if (!prev || !next) return next;

      // bounds-driven location
      if ("bounds" in prev && "bounds" in next) {
        const prevB = prev.bounds;
        const nextB = next.bounds;
        const sameBounds =
          Array.isArray(prevB) &&
          Array.isArray(nextB) &&
          prevB.length === 2 &&
          nextB.length === 2 &&
          prevB[0]?.[0] === nextB[0]?.[0] &&
          prevB[0]?.[1] === nextB[0]?.[1] &&
          prevB[1]?.[0] === nextB[1]?.[0] &&
          prevB[1]?.[1] === nextB[1]?.[1];

        return sameBounds ? prev : next;
      }

      // center+zoom-driven location
      if ("center" in prev && "center" in next) {
        const prevC = prev.center;
        const nextC = next.center;
        const sameCenter =
          Array.isArray(prevC) &&
          Array.isArray(nextC) &&
          prevC.length === 2 &&
          nextC.length === 2 &&
          prevC[0] === nextC[0] &&
          prevC[1] === nextC[1];

        const prevZ = prev.zoom;
        const nextZ = next.zoom;
        const sameZoom =
          typeof prevZ === "number" &&
          typeof nextZ === "number" &&
          prevZ === nextZ;

        return sameCenter && sameZoom ? prev : next;
      }

      // switching between bounds and center/zoom
      return next;
    });
  }, []);

  // Check if Yandex Maps is already globally initialized
  const [isInitialized, setIsInitialized] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!(window as unknown as { __yandexMapsInitialized?: boolean })
      .__yandexMapsInitialized;
  });

  const handleScriptLoad = () => {
    console.log("Yandex Maps script loaded");
    setScriptLoaded(true);
  };

  const handleScriptError = () => {
    console.error("Failed to load Yandex Maps script");
    setContextValue((prev) => ({
      ...prev,
      error: "Failed to load Yandex Maps script",
    }));
  };

  useEffect(() => {
    setContextValue((prev) => {
      return { ...prev, location };
    });
  }, [location]);

  useEffect(() => {
    // If Yandex Maps is already globally initialized, restore the context
    if (typeof window !== "undefined" && isInitialized && window.ymaps3) {
      const restoreYMapsContext = async () => {
        try {
          await window.ymaps3.ready;

          // Check if we already have cached reactify instances
          const globalCache = (
            window as unknown as {
              __yandexMapsCache?: {
                YMap: any;
                YMapDefaultSchemeLayer: any;
                YMapDefaultFeaturesLayer: any;
                YMapMarker: any;
                YMapListener: any;
                YMapControls: any;
                ZoomControl: any;
                GeolocationControl: any;
                reactify: any;
              };
            }
          ).__yandexMapsCache;
          if (globalCache) {
            setContextValue({
              isLoaded: true,
              error: null,
              ...globalCache,
              setCenter,
              location,
            });
            return;
          }

          const [ymaps3React] = await Promise.all([
            window.ymaps3.import("@yandex/ymaps3-reactify"),
            window.ymaps3.ready,
          ]);

          const { YMapZoomControl, YMapGeolocationControl } =
            await window.ymaps3.import("@yandex/ymaps3-controls@0.0.1");

          const reactify = ymaps3React.reactify.bindTo(React, ReactDOM);

          const {
            YMap,
            YMapDefaultSchemeLayer,
            YMapDefaultFeaturesLayer,
            YMapMarker,
            YMapListener,
            YMapControls,
          } = reactify.module(window.ymaps3);

          const ZoomControl = reactify.entity(YMapZoomControl);
          const GeolocationControl = reactify.entity(YMapGeolocationControl);

          const cacheData = {
            YMap,
            YMapDefaultSchemeLayer,
            YMapDefaultFeaturesLayer,
            YMapMarker,
            YMapListener,
            YMapControls,
            ZoomControl,
            GeolocationControl,
            reactify,
          };

          // Cache the instances globally
          (
            window as unknown as { __yandexMapsCache?: typeof cacheData }
          ).__yandexMapsCache = cacheData;

          setContextValue({
            isLoaded: true,
            error: null,
            ...cacheData,
            setCenter,
            location,
          });
        } catch (error) {
          console.error("Failed to restore Yandex Maps context:", error);
          setContextValue((prev) => ({
            ...prev,
            error:
              error instanceof Error ? error.message : "Unknown error occurred",
          }));
        }
      };

      restoreYMapsContext();
    }
  }, [isInitialized, location, setCenter]);

  useEffect(() => {
    if (!scriptLoaded) return;

    const initializeYMaps = async () => {
      try {
        if (!window.ymaps3) {
          throw new Error("YMaps3 not found in window");
        }

        await window.ymaps3.ready;

        const [ymaps3React] = await Promise.all([
          window.ymaps3.import("@yandex/ymaps3-reactify"),
          window.ymaps3.ready,
        ]);

        const { YMapZoomControl, YMapGeolocationControl } =
          await window.ymaps3.import("@yandex/ymaps3-controls@0.0.1");

        const reactify = ymaps3React.reactify.bindTo(React, ReactDOM);

        const {
          YMap,
          YMapDefaultSchemeLayer,
          YMapDefaultFeaturesLayer,
          YMapMarker,
          YMapListener,
          YMapControls,
        } = reactify.module(window.ymaps3);

        const ZoomControl = reactify.entity(YMapZoomControl);
        const GeolocationControl = reactify.entity(YMapGeolocationControl);

        const cacheData = {
          YMap,
          YMapDefaultSchemeLayer,
          YMapDefaultFeaturesLayer,
          YMapMarker,
          YMapListener,
          YMapControls,
          ZoomControl,
          GeolocationControl,
          reactify,
        };

        // Cache the instances globally
        (
          window as unknown as { __yandexMapsCache?: typeof cacheData }
        ).__yandexMapsCache = cacheData;

        setContextValue({
          isLoaded: true,
          error: null,
          ...cacheData,
          setCenter,
          location,
        });
        setIsInitialized(true);
        // Set global flag to mark Yandex Maps as initialized
        (
          window as unknown as { __yandexMapsInitialized?: boolean }
        ).__yandexMapsInitialized = true;
      } catch (error) {
        console.error("Failed to initialize Yandex Maps:", error);
        setContextValue((prev) => ({
          ...prev,
          error:
            error instanceof Error ? error.message : "Unknown error occurred",
        }));
      }
    };

    if (typeof window !== "undefined" && window.ymaps3 && !isInitialized) {
      initializeYMaps();
    } else if (
      typeof window !== "undefined" &&
      window.ymaps3 &&
      isInitialized
    ) {
      // If already initialized, just update the context with current values
      setContextValue((prev) => ({
        ...prev,
        location,
      }));
    } else if (typeof window !== "undefined") {
      const checkYMaps = () => {
        if (window.ymaps3 && !isInitialized) {
          initializeYMaps();
        } else if (window.ymaps3 && isInitialized) {
          // If already initialized, just update the context with current values
          setContextValue((prev) => ({
            ...prev,
            location,
          }));
        } else {
          setTimeout(checkYMaps, 100);
        }
      };
      checkYMaps();
    }
  }, [scriptLoaded, isInitialized, location, setCenter]);

  return (
    <>
      <Script
        id="yandex-maps"
        src={`https://api-maps.yandex.ru/v3/?apikey=${process.env.NEXT_PUBLIC_YANDEX_MAPS_API_KEY}&lang=ru_RU`}
        strategy="afterInteractive"
        onLoad={handleScriptLoad}
        onError={handleScriptError}
      />
      <YMapsContext.Provider value={contextValue}>
        {children}
      </YMapsContext.Provider>
    </>
  );
};
